/**
 * Quick-Nav Registry (Cabinet) — curated CORE features per cabinet context.
 *
 * Feeds the <ContextQuickNav> bar rendered just under the TopBar: 5-8 flagship
 * pages per context (associé, directeur de mission, consultant GRC, ...) so
 * users are never lost in the huge left nav. This is a SHORTCUT layer, not a
 * copy of the sidebar — curate ruthlessly.
 *
 * Curating rules:
 *  - 5 to 8 items max per context (MAX_QUICK_NAV_ITEMS enforced).
 *  - hrefs MUST exist in contexts.ts SECTION_NAV_ITEMS (same routes as sidebar).
 *  - `badge` is an optional attention chip (e.g. 'Nouveau' on create surfaces).
 *  - Uncurated contexts auto-derive the first leaf routes of their nav tree.
 */

import {
  LayoutDashboard, Building2, FolderKanban, FolderOpen, CreditCard,
  Target, Globe, Calendar, Gauge, AlertTriangle, FileText,
  BarChart3, Scale, Microscope, ClipboardCheck, Shield, Siren,
  Bug, FileCheck, Crosshair, Award, RefreshCw, Puzzle, BadgeCheck,
  Boxes, Users, Eye, Map, Lightbulb, Rocket, TrendingUp,
  Presentation, GraduationCap, Megaphone, Sparkles, Lock, Plug,
  KeyRound, Crown, Grid2x2,
  type LucideIcon,
} from 'lucide-react'

import {
  getNavSectionsForContext,
  type GrcContextId,
  type NavItem,
} from '@/config/contexts'
import { tn } from '@/i18n/navLabels'

export interface QuickNavItem {
  title: string
  icon: LucideIcon
  href: string
  /** Optional attention chip (keep to 1-2 per context), e.g. 'Nouveau'. */
  badge?: string
}

export const MAX_QUICK_NAV_ITEMS = 8

// ─── Curated flagship pages per cabinet context ──────────────────────────────

const QUICK_NAV: Record<GrcContextId, QuickNavItem[]> = {

  // Portefeuille, revenus, prospection
  associe: [
    { title: 'Dashboard Cabinet', icon: LayoutDashboard, href: '/dashboard' },
    { title: 'Clients', icon: Building2, href: '/clients' },
    { title: 'Pipeline', icon: FolderKanban, href: '/prospection' },
    { title: 'Nouveau Mandat', icon: FolderOpen, href: '/mandats/new', badge: 'Nouveau' },
    { title: 'Facturation', icon: CreditCard, href: '/billing' },
    { title: 'Cockpit OKR', icon: Target, href: '/strategy-pro/cockpit' },
    { title: 'Panorama', icon: Globe, href: '/dashboard-pro/panorama' },
  ],

  // Mandats, supervision, livrables
  'directeur-mission': [
    { title: 'Mandats', icon: FolderOpen, href: '/mandats' },
    { title: 'Nouveau Mandat', icon: FolderKanban, href: '/mandats/new', badge: 'Nouveau' },
    { title: 'Calendrier', icon: Calendar, href: '/global/calendar' },
    { title: 'Cockpit PSA', icon: Gauge, href: '/dashboard-pro/psa' },
    { title: 'Constats', icon: AlertTriangle, href: '/audit-pro/findings' },
    { title: 'Clients', icon: Building2, href: '/clients' },
    { title: 'Documents', icon: FileText, href: '/global/documents' },
  ],

  // Risques, conformité, gouvernance
  'consultant-grc': [
    { title: 'Cockpit GRC', icon: BarChart3, href: '/governance-pro/cockpit' },
    { title: 'Politiques', icon: FileText, href: '/governance-pro/policies' },
    { title: 'Registre Risques', icon: AlertTriangle, href: '/risk-pro/register' },
    { title: 'Conformité', icon: Scale, href: '/compliance-pro/cockpit' },
    { title: 'Gap Analysis', icon: Microscope, href: '/compliance-pro/gap-analysis' },
    { title: 'Évaluations', icon: ClipboardCheck, href: '/maturity-pro/questionnaires' },
    { title: 'TPRM', icon: Building2, href: '/vendor-pro/cockpit' },
  ],

  // Audits techniques, ISO 27001, incidents
  'expert-securite': [
    { title: 'Hub Sécurité', icon: Shield, href: '/security-pro' },
    { title: 'Incidents', icon: Siren, href: '/security-pro/incidents' },
    { title: 'Vulnérabilités', icon: Bug, href: '/security-pro/vulns' },
    { title: 'Contrôles', icon: FileCheck, href: '/security-pro/controls' },
    { title: 'Posture', icon: Crosshair, href: '/security-pro/posture' },
    { title: 'ISO 27001', icon: Award, href: '/security-pro/iso27001' },
    { title: 'BCP/DR', icon: RefreshCw, href: '/security-pro/bcp' },
  ],

  // RGPD, data governance, IA responsable
  'expert-donnees-ia': [
    { title: 'Cockpit DAMA', icon: BarChart3, href: '/data-pro/cockpit' },
    { title: 'MDM', icon: Puzzle, href: '/data-pro/mdm' },
    { title: 'Qualité', icon: BadgeCheck, href: '/data-pro/quality' },
    { title: 'Registre Modèles', icon: Boxes, href: '/ai-ia-pro/registre' },
    { title: 'PIA (AIPD)', icon: AlertTriangle, href: '/compliance-pro/pia', badge: 'Nouveau' },
    { title: 'DSR', icon: Users, href: '/compliance-pro/dsr' },
    { title: 'RGPD', icon: Eye, href: '/compliance-pro/rgpd' },
  ],

  // Missions audit, certification, constats
  auditeur: [
    { title: 'Cockpit Audit', icon: BarChart3, href: '/audit-pro/cockpit' },
    { title: 'Missions', icon: ClipboardCheck, href: '/audit-pro/missions' },
    { title: 'Plan Annuel', icon: Map, href: '/audit-pro/annual-plan' },
    { title: 'Wizard Audit', icon: Lightbulb, href: '/audit-pro/plan-wizard', badge: 'Nouveau' },
    { title: 'Constats', icon: AlertTriangle, href: '/audit-pro/findings' },
    { title: 'Preuves', icon: FileCheck, href: '/audit-pro/evidence' },
    { title: 'Certification', icon: Award, href: '/audit-pro/cert-dashboard' },
  ],

  // Évaluations, maturité, benchmarks
  analyste: [
    { title: 'Cockpit Maturité', icon: Gauge, href: '/maturity-pro/cockpit' },
    { title: 'Évaluations', icon: ClipboardCheck, href: '/maturity-pro/questionnaires' },
    { title: 'Campagnes', icon: Rocket, href: '/maturity-pro/campaigns' },
    { title: 'Benchmarks', icon: BarChart3, href: '/dashboard-pro/benchmarks' },
    { title: 'Tendances', icon: TrendingUp, href: '/dashboard-pro/tendances' },
    { title: 'Heatmap', icon: Grid2x2, href: '/dashboard-pro/heatmap' },
    { title: 'Rapport CA', icon: Presentation, href: '/dashboard-pro/rapport-ca' },
  ],

  // Sensibilisation, changement, adoption
  formateur: [
    { title: 'Cockpit Changement', icon: BarChart3, href: '/change-pro/cockpit' },
    { title: 'Formation', icon: GraduationCap, href: '/change-pro/formation' },
    { title: 'Communication', icon: Megaphone, href: '/change-pro/communication' },
    { title: 'Adoption', icon: TrendingUp, href: '/change-pro/adoption' },
    { title: 'ADKAR', icon: Sparkles, href: '/change-pro/adkar-assessment' },
    { title: 'Roadmap', icon: Map, href: '/change-pro/roadmap' },
    { title: 'Culture Éthique', icon: GraduationCap, href: '/ethics-pro/culture' },
  ],

  // Organisation, équipe, facturation
  administration: [
    { title: 'Équipe', icon: Users, href: '/team' },
    { title: 'Rôles', icon: Lock, href: '/team/roles' },
    { title: 'Organisations', icon: Building2, href: '/admin/organizations' },
    { title: 'Facturation', icon: CreditCard, href: '/billing' },
    { title: 'Abonnement', icon: Crown, href: '/account/subscription' },
    { title: 'Intégrations', icon: Plug, href: '/admin/integrations' },
    { title: 'Clés API', icon: KeyRound, href: '/admin/api-keys' },
  ],
}

// ─── Fallback derivation + accessor ─────────────────────────────────────────

/**
 * Fallback for uncurated/unknown contexts: first MAX leaf routes across the
 * context's visible nav sections (deduped), so the bar never renders empty.
 */
function deriveFromNavTree(context: GrcContextId): QuickNavItem[] {
  const out: QuickNavItem[] = []
  const seen = new Set<string>()
  const walk = (items: NavItem[]) => {
    for (const item of items) {
      if (out.length >= MAX_QUICK_NAV_ITEMS) return
      if (item.href && !seen.has(item.href)) {
        seen.add(item.href)
        out.push({ title: item.title, icon: item.icon, href: item.href })
      }
      if (item.children) walk(item.children)
    }
  }
  for (const section of getNavSectionsForContext(context)) {
    if (out.length >= MAX_QUICK_NAV_ITEMS) break
    walk(section.items)
  }
  return out
}

/** Quick-nav items for a context — curated first, auto-derived otherwise.
 *  Curated titles/badges are localized here; auto-derived items already come
 *  localized from `getNavSectionsForContext`. */
export function getQuickNavForContext(context: GrcContextId): QuickNavItem[] {
  const curated = QUICK_NAV[context]
  if (curated && curated.length > 0) {
    return curated.slice(0, MAX_QUICK_NAV_ITEMS).map((it) => ({
      ...it,
      title: tn(it.title),
      ...(it.badge ? { badge: tn(it.badge) } : {}),
    }))
  }
  return deriveFromNavTree(context)
}
